import * as React from 'react'
import { StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { onlyUniqueByReference } from '../../../../services/utils/lodash.service'
import { Select } from '../../../shared/select.component'

type AllowedAttr = StoreProps['data']['offeredService']['allowed_offered_service_attrs'][0];
type Combination = StoreProps['data']['offeredService']['offered_service_combinations'][0];
const i18nScoped = new I18nService(require('./casting-combination-attributes-picker.component.yml'))

type Props = StoreProps & {
  allowedOfferedServiceAttrs: AllowedAttr[]
}

export default function CastingCombinationAttributesPickerComponent ({ store, data: { selectedCombinationAttrs }, allowedOfferedServiceAttrs }: Props) {
  const getValuesForAllowedAttr = React.useCallback((allowedAttr: AllowedAttr, combinations: Combination[]) => {
    return combinations.flatMap(c => c.offered_service_combination_attrs)
      .filter(combinationAttr => combinationAttr.allowed_offered_service_attr_id === allowedAttr.id)
      .flatMap(combinationAttr => combinationAttr.values)
      .filter(onlyUniqueByReference)
  }, [])

  const selectAllowedAttr = React.useCallback((allowedAttr: AllowedAttr, value: string) => {
    const existing = selectedCombinationAttrs.find(_ => _.allowed_offered_service_attr_id === allowedAttr.id)

    if (existing && (value || existing.id)) {
      store.setSelectedCombinationAttrs(selectedCombinationAttrs.map(_ => _ === existing ? { ...existing, value, _destroy: !value } : _))
    } else if (!value) {
      store.setSelectedCombinationAttrs(selectedCombinationAttrs.filter(_ => _.allowed_offered_service_attr_id !== allowedAttr.id))
    } else {
      store.setSelectedCombinationAttrs(selectedCombinationAttrs.concat({ value, allowed_offered_service_attr_id: allowedAttr.id }))
    }
  }, [selectedCombinationAttrs, store])
  
  const renderAttributes = React.useCallback((allowedAttrs: AllowedAttr[]) => {
    return allowedAttrs.map(allowedAttr => {
      const combinations = store.getCombinationsMatchingSelectedAttrs([allowedAttr.id])
      const selected = selectedCombinationAttrs.find(_ => !_._destroy && _.allowed_offered_service_attr_id === allowedAttr.id)
      const values = getValuesForAllowedAttr(allowedAttr, combinations)
        .sort((a, b) => allowedAttr.offered_service_attr.field_type === 'number' ? +a - +b : a.localeCompare(b))
        .map(value => ({ value, label: `${allowedAttr.offered_service_attr.name} : ${value}` }))

      return <div key={ allowedAttr.id } id={ `js-test-allowed-attr-${allowedAttr.id}` } style={{ paddingBottom: '0.5em' }}>
        <Select
          value={ values.find(_ => _.value === selected?.value) }
          options={ values }
          placeholder={ allowedAttr.offered_service_attr.name }
          onChange={ val => selectAllowedAttr(allowedAttr, val?.value ?? null) }
          isClearable={ !allowedAttr.required }
          isSearchable={ false } />
      </div>
    })
  }, [getValuesForAllowedAttr, selectAllowedAttr, selectedCombinationAttrs, store])

  const renderedAllowedAttrs = allowedOfferedServiceAttrs.filter(_ => !_.deactivated).sort((a, b) => a.position - b.position)
  const requiredAllowedAttrs = renderedAllowedAttrs.filter(_ => _.required)
  const optionalAllowedAttrs = renderedAllowedAttrs.filter(_ => !_.required)

  return <>
    { requiredAllowedAttrs.length > 0 && <>
      <p>{ i18nScoped.t('required_allowed_attrs') }</p>
      { renderAttributes(requiredAllowedAttrs) }
    </> }

    { optionalAllowedAttrs.length > 0 && <>
      <p>{ i18nScoped.t('optional_allowed_attrs') }</p>
      { renderAttributes(optionalAllowedAttrs) }
    </> }
  </>
}
